import { getUserCreds, socket } from "@/utils/utils"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import { MainButton } from "@/Components/MainButton"
import ModalButton from "@/Components/ModalButton"
import Color from "color"
import React, { useState } from "react"
import Money from "@/Components/Money"
import TradeMoneySlider from "@/Pages/Trade/TradeMoneySlider"
import ModalCloseButton from "@/Components/ModalCloseButton"
import { images } from "@/utils/data"

const StockModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const modalData = useAppSelector((state) => state.global.modalData)
  const notifications = useAppSelector((state) => state.global.notifications)
  const actionCardAnimationRunning = useAppSelector(
    (state) => state.global.actionCardAnimationRunning
  )
  const [investmentAmount, setInvestmentAmount] = useState<number>(0)
  const activePlayer = myGame?.situation?.players.find(
    (p) => p.id === myGame?.situation?.turn
  )

  if (
    !modalData.stockExchange?.visible ||
    activePlayer?.id !== getUserCreds().email
  )
    return null

  const handleInvest = () => {
    if (investmentAmount <= 0 || investmentAmount > activePlayer.money) return

    socket.emit("game.stock-response", {
      roomid: myGame!.id,
      action: "invested",
      money: investmentAmount,
    })
    dispatch(setModalData({ stockExchange: { visible: false } }))
  }

  const isWolf = activePlayer.role === "wall-street-wolf"
  const growthRate = isWolf ? 0.6 : 0.3

  // The ladder is only ever about the money being put in right now — folding an
  // older stake into it makes "After 1 round" read as the current cash-out.
  const projections = [
    {
      label: "After 1 round",
      total: Math.ceil(investmentAmount * (1 + growthRate)),
      percent: Math.round(growthRate * 100),
    },
    {
      label: "After 2 rounds",
      total: Math.ceil(investmentAmount * (1 + growthRate * 2)),
      percent: Math.round(growthRate * 200),
    },
  ]

  // An earlier stake, shown on its own: what it is worth today and how much
  // growth it has left before it stops at 1 + growthRate * 2.
  const retrievableNow = Math.ceil(
    activePlayer.stock * activePlayer.stockMultiplier
  )
  const growthAlreadyEarned = retrievableNow - activePlayer.stock
  const fullyGrownValue = Math.ceil(activePlayer.stock * (1 + growthRate * 2))
  const roundsOfGrowthLeft =
    activePlayer.stockMultiplier >= 1 + growthRate * 2 - 0.001
      ? 0
      : activePlayer.stockMultiplier > 1
      ? 1
      : 2

  const handleClose = () => {
    socket.emit("game.stock-response", {
      roomid: myGame!.id,
      action: "dismissed",
      money: 0,
    })
    dispatch(setModalData({ stockExchange: { visible: false } }))
  }

  const handleRetrieve = () => {
    socket.emit("game.stock-response", {
      roomid: myGame!.id,
      action: "retrieved",
      money: 0,
    })
    dispatch(setModalData({ stockExchange: { visible: false } }))
  }
  const isAnyModalOpen =
    modalData.auction?.visible ||
    modalData.buyOrAuction?.visible ||
    modalData.forcedBuy?.visible ||
    modalData.hiddenAuction?.visible ||
    modalData.doubleRent?.visible ||
    modalData.sayNo?.visible ||
    modalData.decisionHub?.visible
  const anyNotificaionOpen = notifications.length > 0
  // const propertyTransferAnimationRunning =
  //   animationData?.propertyMoveAnimations?.length !== 0

  if (isAnyModalOpen || anyNotificaionOpen || actionCardAnimationRunning !== 0)
    return null

  return (
    <div
      className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw]  max-w-3xl bg-black/90 backdrop-blur-xl border border-gray-700/60 shadow-2xl z-[110] p-6 md:p-8 sm:p-4 rounded-2xl flex flex-col"
      style={{ boxShadow: "0 8px 32px 0 rgba(0,0,0,0.45)" }}
    >
      <div className="flex flex-col gap-5 md:gap-6 sm:gap-3">
        {/* Header — title, the rules in one line, and live cash */}
        <div className="flex justify-between items-start gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={images.icons.stock}
              alt="stock"
              className="w-7 h-7 md:w-9 md:h-9 flex-shrink-0"
            />
            <div className="flex flex-col min-w-0">
              <h2 className="text-3xl md:text-4xl font-poppins font-bold text-white tracking-wide leading-none">
                Stock Exchange
              </h2>
              <p className="font-poppins text-lg md:text-base text-gray-400 leading-snug mt-1 sm:hidden">
                Grows{" "}
                <span className="text-orange-300 font-semibold">
                  +{Math.round(growthRate * 100)}% a round, for 2 rounds
                </span>
                {isWolf && (
                  <span className="text-green-300 font-semibold">
                    {" "}
                    (Wolf 2×)
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <ModalCloseButton onClick={handleClose} />
          </div>
        </div>

        {/* Investing — the slider, the amount it sets, what that amount pays
            back, and the button that spends it, all in one group. A shared
            button row at the bottom made the slider look like it fed both
            actions, which is how players came to expect a partial withdraw. */}
        <div className="flex flex-col gap-5 md:gap-6 sm:gap-3">
          {/* Slider — its floating label is the live "amount to invest" readout */}
          <TradeMoneySlider
            className="w-full"
            max={activePlayer.money}
            value={investmentAmount}
            setValue={setInvestmentAmount}
            min={0}
            minTestId="auction.money-slider-min"
            maxTestId="auction.money-slider-max"
          />

          {/* Amount box — same state as the slider, so the two stay in sync */}
          <label className="cursor-text mx-auto flex w-fit items-center justify-center gap-1 rounded-lg border-2 border-gray-600 bg-gray-800/60 px-3 py-1 transition-colors focus-within:border-orange-500/60">
            <span className="font-fira-sans text-2xl stock-modal-money">M</span>
            <input
              type="number"
              className="stock-amount-input w-[6ch] max-w-full bg-transparent border-0 p-0 text-2xl font-poppins font-bold text-white focus:outline-none"
              value={Number.isFinite(investmentAmount) ? investmentAmount : 0}
              min={0}
              max={activePlayer.money}
              step={1}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === "") {
                  setInvestmentAmount(0)
                  return
                }
                const parsed = Math.floor(Number(raw))
                if (Number.isNaN(parsed)) {
                  return
                }
                const clamped = Math.min(
                  Math.max(parsed, 0),
                  Math.floor(activePlayer.money)
                )
                setInvestmentAmount(clamped)
              }}
            />
          </label>

          {/* What this investment pays back, round by round */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-center gap-1.5 font-poppins text-lg md:text-sm text-gray-300">
              <span>If you invest</span>
              <Money
                money={investmentAmount}
                className="text-xl gap-1 font-bold stock-modal-money"
              />
              <span>you can withdraw</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {projections.map(({ label, total, percent }) => (
                <div
                  key={label}
                  className="rounded-lg border border-green-400/25 bg-green-400/5 px-3 py-1.5 flex flex-col items-center"
                >
                  <span className="font-poppins text-base md:text-xs text-gray-400">
                    {label}
                  </span>
                  <Money
                    money={total}
                    className="text-2xl gap-1 font-poppins font-bold stock-modal-money"
                  />
                  <span className="font-poppins text-sm md:text-xs font-semibold text-green-400">
                    +{percent}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* The amount rides on the button: dragging the slider moves this
              number and leaves the withdraw one untouched, so the split is
              something players discover by playing rather than by reading. */}
          <ModalButton
            onClick={handleInvest}
            disabled={
              investmentAmount <= 0 || investmentAmount > activePlayer.money
            }
            className="w-full sm:py-2.5"
            color="green"
          >
            {/* ModalButton wraps children in its own span, so the gap has to
                live on a wrapper of our own — not on the button's className. */}
            <span className="flex items-center justify-center gap-3">
              <i className="fa-solid fa-chart-line"></i>
              <span>Invest</span>
              <Money money={investmentAmount} className="gap-1" />
            </span>
          </ModalButton>
        </div>

        {/* Money already invested, laid out like the projection above — same
            caption line, same cards — so the two blocks read as one family,
            while the blue keeps its value from being mistaken for the new one. */}
        {activePlayer.stock > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-center gap-1.5 font-poppins text-lg md:text-sm text-gray-300">
              <i className="fa-solid fa-sack-dollar text-blue-300"></i>
              <span>You already have</span>
              <Money
                money={activePlayer.stock}
                className="text-xl gap-1 font-bold stock-modal-money"
              />
              <span>invested</span>
            </div>

            <div
              className={`grid gap-2 ${
                roundsOfGrowthLeft === 0 ? "grid-cols-1" : "grid-cols-2"
              }`}
            >
              <div className="rounded-lg border border-blue-400/25 bg-blue-400/5 px-3 py-1.5 flex flex-col items-center">
                <span className="font-poppins text-base md:text-xs text-gray-400">
                  Withdraw now
                </span>
                <Money
                  money={retrievableNow}
                  className="text-2xl gap-1 font-poppins font-bold stock-modal-money"
                />
                <span className="font-poppins text-sm md:text-xs font-semibold text-blue-300">
                  {growthAlreadyEarned > 0 ? (
                    <>
                      +<span className="font-fira-sans font-normal">M</span>
                      {growthAlreadyEarned} earned
                    </>
                  ) : roundsOfGrowthLeft === 0 ? (
                    "fully grown"
                  ) : (
                    "not grown yet"
                  )}
                </span>
              </div>

              {roundsOfGrowthLeft > 0 && (
                <div className="rounded-lg border border-blue-400/25 bg-blue-400/5 px-3 py-1.5 flex flex-col items-center">
                  <span className="font-poppins text-base md:text-xs text-gray-400">
                    After {roundsOfGrowthLeft} more round
                    {roundsOfGrowthLeft > 1 ? "s" : ""}
                  </span>
                  <Money
                    money={fullyGrownValue}
                    className="text-2xl gap-1 font-poppins font-bold stock-modal-money"
                  />
                  <span className="font-poppins text-sm md:text-xs font-semibold text-blue-300">
                    then it stops
                  </span>
                </div>
              )}
            </div>

            {/* Investing merges the two stakes and resets the multiplier to 1,
                so any growth the old stake earned is forfeited. */}
            {investmentAmount > 0 && growthAlreadyEarned > 0 && (
              <p className="flex items-start gap-2 font-poppins text-lg md:text-sm text-red-300/90 leading-snug">
                <i className="fa-solid fa-triangle-exclamation mt-0.5"></i>
                <span>
                  Investing restarts this one too — you would give up the{" "}
                  <span className="font-fira-sans font-normal">M</span>
                  {growthAlreadyEarned} it has already earned. Withdraw first to
                  keep it.
                </span>
              </p>
            )}

            {/* Lives inside the stake block, not in a shared row — it acts on
                this money only, and "all" plus a fixed amount is the whole
                answer to "can I take out just some of it?". */}
            <ModalButton
              onClick={handleRetrieve}
              className="w-full mt-2 sm:py-2.5"
              color="blue"
              disabled={retrievableNow <= 0}
            >
              <span className="flex items-center justify-center gap-3">
                <i className="fa-solid fa-hand-holding-dollar"></i>
                <span>Withdraw all</span>
                <Money money={retrievableNow} className="gap-1" />
              </span>
            </ModalButton>
          </div>
        )}
      </div>
    </div>
  )
}

export default StockModal
